import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { HelmetProvider } from 'react-helmet-async';
import { User, LessonPlan, Region } from './types';
import { CheckoutSuccess } from './views/CheckoutSuccess';
import { Terms } from './views/Terms';
import { Privacy } from './views/Privacy';
import { Contact } from './views/Contact';
import { supabase } from './services/supabase';
import { Loader2 } from 'lucide-react';
import { runWhenIdle, hasDeferredFetchRun, markDeferredFetchComplete, clearDeferredFetches } from './src/utils/deferredFetch';
import { notifyUserSignup } from './services/slackService';
import { createDebugLogger } from './src/utils/debug';
import { trackPageView } from './src/services/analytics';

const debug = createDebugLogger('App');

// Lazy load marketing pages to reduce initial bundle size
const LandingPage = lazy(() => import('./src/pages/marketing/LandingPage').then(m => ({ default: m.LandingPage })));
const FeaturesPage = lazy(() => import('./src/pages/marketing/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const HowItWorksPage = lazy(() => import('./src/pages/marketing/HowItWorksPage').then(m => ({ default: m.HowItWorksPage })));
const TEKSAlignmentPage = lazy(() => import('./src/pages/marketing/TEKSAlignmentPage').then(m => ({ default: m.TEKSAlignmentPage })));
const PricingPage = lazy(() => import('./src/pages/marketing/PricingPage').then(m => ({ default: m.PricingPage })));

// Lazy load heavy app routes
const Auth = lazy(() => import('./views/Auth').then(m => ({ default: m.Auth })));
const Onboarding = lazy(() => import('./views/Onboarding_SIMPLE').then(m => ({ default: m.Onboarding })));
const Dashboard = lazy(() => import('./views/Dashboard').then(m => ({ default: m.Dashboard })));
const LessonBuilder = lazy(() => import('./views/LessonBuilder').then(m => ({ default: m.LessonBuilder })));
const LessonViewer = lazy(() => import('./views/LessonViewer').then(m => ({ default: m.LessonViewer })));
const Account = lazy(() => import('./views/Account').then(m => ({ default: m.Account })));

// Simple wrapper to handle routing logic based on auth state
const MainController: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [session, setSession] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [lessons, setLessons] = useState<LessonPlan[]>([]);
  const [viewingLesson, setViewingLesson] = useState<LessonPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lessonsLoaded, setLessonsLoaded] = useState(false);
  const [isFetchingLesson, setIsFetchingLesson] = useState(false);

  // Track page views for Google Analytics
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // 1. Handle Auth Session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchUserProfile(session.user.id);
      } else {
        setIsLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchUserProfile(session.user.id);
      } else {
        setUser(null);
        setLessons([]);
        setLessonsLoaded(false);
        setIsLoading(false);

        // Reset analytics on logout
        import('./src/services/analytics').then(({ resetAnalytics }) => {
          resetAnalytics();
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // 2. Fetch User Profile (optimized: only essential fields for boot)
  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error} = await supabase
        .from('profiles')
        .select('first_name,last_name,school_district,school_name,region,grade_level,subject,is_onboarded')
        .eq('id', userId)
        .single();

      if (error) {
        // PGRST116: no rows
        // 42P01: table missing in dev
        if (error.code !== 'PGRST116' && error.code !== '42P01') {
          console.error('Error fetching profile:', error.message || JSON.stringify(error));
        }
      }

      if (data) {
        setUser({
          id: userId, // Include Supabase user ID for usage tracking
          email: session?.user?.email || '',
          firstName: data.first_name,
          lastName: data.last_name,
          schoolDistrict: data.school_district,
          schoolName: data.school_name,
          region: data.region as Region,
          gradeLevel: data.grade_level,
          subject: data.subject as any,
          isOnboarded: data.is_onboarded,
        });

        // Identify user in analytics after authentication (PostHog initialized in index.tsx)
        // Include non-PII user properties for segmentation
        runWhenIdle(() => {
          import('./src/services/analytics').then(({ identify }) => {
            identify(userId, {
              region: data.region,
              grade_level: data.grade_level,
              subject: data.subject,
              is_onboarded: data.is_onboarded,
            });
          });
        });

        // Defer lessons fetch until after first paint (LCP optimization)
        // Don't fetch lessons on /create route - not needed there
        if (data.is_onboarded && !hasDeferredFetchRun('lessons', userId)) {
          runWhenIdle(() => {
            // Only fetch lessons if on dashboard route
            const currentPath = window.location.pathname;
            if (currentPath.includes('/dashboard') || currentPath === '/app') {
              fetchLessons(userId).then(() => {
                setLessonsLoaded(true);
                markDeferredFetchComplete('lessons', userId);
              });
            } else {
              // On other routes (like /create), just mark as loaded without fetching
              setLessonsLoaded(true);
              markDeferredFetchComplete('lessons', userId);
            }
          });
        } else {
          setLessonsLoaded(true);
        }

        setIsLoading(false);
      } else {
        // New user or row hidden
        setUser({
          id: userId, // Include Supabase user ID for usage tracking
          email: session?.user?.email || '',
          region: '' as any,
          gradeLevel: '',
          subject: '' as any,
          isOnboarded: false,
        });
        setLessonsLoaded(true);
        setIsLoading(false);
      }
    } catch (error: any) {
      console.error('Profile fetch error', error.message || error);
      setLessonsLoaded(true);
      setIsLoading(false);
    }
  };

  // 3. Fetch Lessons (optimized: minimal fields, limit 20)
  const fetchLessons = async (userId: string) => {
    debug('Fetching lessons');
    const { data, error } = await supabase
      .from('lessons')
      .select('id,title,grade,subject,standards,created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error && error.code !== '42P01') {
      console.error('Error fetching lessons:', error.message || error);
    }

    if (data) {
      debug('Fetched lessons', { count: data.length });
      const mappedLessons: LessonPlan[] = data.map((l: any) => ({
        id: l.id,
        title: l.title,
        grade: l.grade,
        subject: l.subject,
        duration: l.duration || '', // Not fetched in minimal query
        standards: l.standards || [], // Not fetched in minimal query
        content: l.content || {}, // Not fetched in minimal query
        createdAt: l.created_at,
      }));
      setLessons(mappedLessons);
    }
  };

  // Refetch lessons when navigating to dashboard
  React.useEffect(() => {
    if (session?.user && user?.isOnboarded && lessonsLoaded) {
      if (location.pathname.includes('/dashboard') || location.pathname === '/app') {
        debug('Refetching lessons on dashboard navigation');
        fetchLessons(session.user.id);
      }
    }
  }, [location.pathname, session?.user?.id, user?.isOnboarded, lessonsLoaded]);

  // 4. Onboarding complete – always move forward, Supabase is best effort
  const handleOnboardingComplete = async (data: Partial<User>) => {
    if (!session?.user) {
      console.error('Cannot complete onboarding: no session');
      return;
    }

    debug('Onboarding complete clicked', {
      hasFirstName: !!data.firstName,
      hasLastName: !!data.lastName,
      hasSchoolDistrict: !!data.schoolDistrict,
      hasSchoolName: !!data.schoolName,
      hasRegion: !!data.region,
      hasGradeLevel: !!data.gradeLevel,
      hasSubject: !!data.subject,
    });

    // CRITICAL FIX: Show loading state immediately
    setIsLoading(true);

    try {
      // Build upsert payload
      const upsertPayload = {
        id: session.user.id,
        email: session.user.email,
        first_name: data.firstName,
        last_name: data.lastName,
        school_district: data.schoolDistrict,
        school_name: data.schoolName,
        region: data.region,
        grade_level: data.gradeLevel,
        subject: data.subject,
        is_onboarded: true,
      };

      debug('Upserting profile');

      // CRITICAL FIX: WAIT for database write before navigation
      const { data: savedProfile, error } = await supabase
        .from('profiles')
        .upsert(upsertPayload, { onConflict: 'id' })
        .select()
        .single();

      // CRITICAL FIX: Handle database errors - don't navigate on failure
      if (error) {
        console.error('[handleOnboardingComplete] Database error:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });

        // Show user-friendly error message
        alert(
          `Unable to save your profile. Please try again.\n\nError: ${error.message}\n\nIf this persists, please contact support.`
        );

        setIsLoading(false);
        return; // CRITICAL: Don't navigate on error
      }

      // Verify we got data back
      if (!savedProfile) {
        console.error('[handleOnboardingComplete] No profile returned from upsert');
        alert('Unable to save your profile. Please try again.');
        setIsLoading(false);
        return;
      }

      debug('Profile saved successfully');

      // Update local state with confirmed database values
      setUser({
        id: session.user.id,
        email: savedProfile.email || session.user.email || '',
        firstName: savedProfile.first_name,
        lastName: savedProfile.last_name,
        schoolDistrict: savedProfile.school_district,
        schoolName: savedProfile.school_name,
        region: savedProfile.region as Region,
        gradeLevel: savedProfile.grade_level || data.gradeLevel || '',
        subject: savedProfile.subject as any,
        isOnboarded: savedProfile.is_onboarded ?? true,
      });

      // Track successful onboarding completion
      import('./src/services/analytics').then(({ track }) => {
        track('onboarding_completed', {
          has_name: !!(savedProfile.first_name && savedProfile.last_name),
          has_school: !!(savedProfile.school_district && savedProfile.school_name),
          grade_level: savedProfile.grade_level,
          subject: savedProfile.subject,
        });
      });

      // Slack: Notify about new user signup
      debug('Sending Slack notification');
      await notifyUserSignup({
        userId: session.user.id,
        email: savedProfile.email || session.user.email || '',
        firstName: savedProfile.first_name,
        lastName: savedProfile.last_name,
        schoolDistrict: savedProfile.school_district,
        schoolName: savedProfile.school_name,
        gradeLevel: savedProfile.grade_level,
        subject: savedProfile.subject,
      });
      debug('Slack notification sent');

      // CRITICAL FIX: Only navigate AFTER successful database write
      debug('Navigating to dashboard');
      setIsLoading(false);
      navigate('/dashboard');

    } catch (e: any) {
      // Handle unexpected errors (network, timeout, etc.)
      console.error('[handleOnboardingComplete] Unexpected error:', e);

      alert(
        `An unexpected error occurred. Please check your internet connection and try again.\n\nError: ${e.message || 'Unknown error'}`
      );

      setIsLoading(false);
      // Don't navigate - keep user on onboarding to retry
    }
  };

  const handleCreateLesson = async (lesson: LessonPlan): Promise<LessonPlan> => {
    if (!session?.user) {
      throw new Error('No active session');
    }

    const { id, createdAt, ...lessonData } = lesson;

    const dbPayload = {
      user_id: session.user.id,
      title: lessonData.title,
      grade: lessonData.grade,
      subject: lessonData.subject,
      duration: lessonData.duration,
      standards: lessonData.standards,
      content: lessonData.content,
    };

    const { data, error } = await supabase
      .from('lessons')
      .insert(dbPayload)
      .select()
      .single();

    if (error) {
      console.error('Error creating lesson:', error.message || error);

      // Missing table (dev)
      if (error.code === '42P01') {
        const newLesson: LessonPlan = {
          ...lesson,
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
        };
        setLessons([newLesson, ...lessons]);
        setViewingLesson(newLesson);
        navigate('/app/view');
        return newLesson;
      }

      // RLS blocking insert: code 42501 or message mentioning row-level security
      if (
        error.code === '42501' ||
        (typeof error.message === 'string' &&
          error.message.toLowerCase().includes('row-level security'))
      ) {
        console.warn('RLS blocked insert, creating lesson only in local state for now');

        const newLesson: LessonPlan = {
          ...lesson,
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
        };
        setLessons([newLesson, ...lessons]);
        setViewingLesson(newLesson);
        navigate('/app/view');
        return newLesson;
      }

      alert('Failed to save lesson.');
      throw error;
    }

    if (data) {
      const newLesson: LessonPlan = {
        id: data.id,
        title: data.title,
        grade: data.grade,
        subject: data.subject,
        duration: data.duration,
        standards: data.standards,
        content: data.content,
        createdAt: data.created_at,
      };
      setLessons([newLesson, ...lessons]);
      setViewingLesson(newLesson);
      navigate('/app/view');
      return newLesson;
    }

    throw new Error('Failed to save lesson - no data returned');
  };


  const handleDeleteLesson = async (id: string) => {
    const { error } = await supabase.from('lessons').delete().eq('id', id);
    if (error && error.code !== '42P01') {
      console.error('Error deleting lesson:', error);
      return;
    }
    setLessons(lessons.filter(l => l.id !== id));
  };

  const handleUpdateLesson = (id: string, updates: Partial<LessonPlan>) => {
    setLessons(lessons.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  const handleViewLesson = async (lesson: LessonPlan) => {
    if (!session?.user) {
      console.error('No active session when viewing lesson');
      return;
    }

    // Fetch full lesson data from Supabase (fetchLessons only loads minimal fields)
    setIsFetchingLesson(true);
    try {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('id', lesson.id)
        .eq('user_id', session.user.id)
        .single();

      if (error) {
        console.error('Error fetching full lesson data:', error);
        alert('Failed to load lesson. Please try again.');
        return;
      }

      if (data) {
        const fullLesson: LessonPlan = {
          id: data.id,
          title: data.title,
          grade: data.grade,
          subject: data.subject,
          duration: data.duration || '',
          standards: data.standards || [],
          content: data.content || { objective: '', materials: '', procedure: '', assessment: '' },
          createdAt: data.created_at,
        };

        setViewingLesson(fullLesson);
        navigate('/app/view');
      }
    } catch (error: any) {
      console.error('Unexpected error fetching lesson:', error);
      alert('Failed to load lesson. Please try again.');
    } finally {
      setIsFetchingLesson(false);
    }
  };

  // Loading - only block on critical data (user profile)
  // Don't block on lessons - they load in the background
  if (isLoading || (session && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Marketing pages - public (at root level) */}
      <Route path="/" element={
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
          <LandingPage />
        </Suspense>
      } />
      <Route path="/features" element={
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
          <FeaturesPage />
        </Suspense>
      } />
      <Route path="/how-it-works" element={
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
          <HowItWorksPage />
        </Suspense>
      } />
      <Route path="/teks-alignment" element={
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
          <TEKSAlignmentPage />
        </Suspense>
      } />
      <Route path="/pricing" element={
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
          <PricingPage />
        </Suspense>
      } />

      {/* Public routes - accessible without login */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />

      {/* Auth-protected routes (under /app) */}
      <Route
        path="/app"
        element={
          !session ? (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
              <Auth />
            </Suspense>
          ) : user && !user.isOnboarded ? (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
              <Onboarding user={user} onComplete={handleOnboardingComplete} />
            </Suspense>
          ) : (
            <Navigate to="/app/dashboard" replace />
          )
        }
      />

      <Route
        path="/app/dashboard"
        element={
          !session ? (
            <Navigate to="/app" replace />
          ) : user && !user.isOnboarded ? (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
              <Onboarding user={user} onComplete={handleOnboardingComplete} />
            </Suspense>
          ) : user ? (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
              <>
                <Dashboard
                  user={user}
                  lessons={lessons}
                  onCreateNew={() => navigate('/app/create')}
                  onViewLesson={handleViewLesson}
                  onDeleteLesson={handleDeleteLesson}
                  onUpdateLesson={handleUpdateLesson}
                  onLogout={async () => {
                    const userId = user?.id;
                    await supabase.auth.signOut();
                    if (userId) {
                      clearDeferredFetches(userId);
                    }
                    setUser(null);
                    setLessons([]);
                    setLessonsLoaded(false);
                    setSession(null);
                  navigate('/');
                }}
              />
              {isFetchingLesson && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 shadow-xl flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-sm text-slate-600">Loading lesson...</p>
                  </div>
                </div>
              )}
            </>
            </Suspense>
          ) : (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          )
        }
      />

      <Route
        path="/app/create"
        element={
          !session || !user ? (
            <Navigate to="/app" replace />
          ) : (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
              <LessonBuilder
                user={user}
                onSave={handleCreateLesson}
                onCancel={() => navigate('/app/dashboard')}
              />
            </Suspense>
          )
        }
      />

      <Route
        path="/app/view"
        element={
          !session || !user ? (
            <Navigate to="/app" replace />
          ) : viewingLesson ? (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
              <LessonViewer
                user={user}
                lesson={viewingLesson}
                onBack={() => navigate('/app/dashboard')}
              />
            </Suspense>
          ) : (
            <Navigate to="/app/dashboard" />
          )
        }
      />

      <Route
        path="/app/account"
        element={
          !session || !user ? (
            <Navigate to="/app" replace />
          ) : (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
              <Account />
            </Suspense>
          )
        }
      />

      <Route path="/signup" element={
        !session ? (
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
            <Auth defaultSignUp />
          </Suspense>
        ) : (
          <Navigate to="/app/dashboard" replace />
        )
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <MainController />
        <SpeedInsights />
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;

